import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leadsAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import LeadGalleryModal from '../components/LeadGalleryModal';
import {
  Target,
  ArrowLeft,
  Edit,
  Save,
  X,
  Phone,
  Mail,
  Building,
  MapPin,
  Calendar,
  User,
  FileText,
  Upload,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  Coins,
  Paperclip,
  Download,
  Image,
  Users,
  Plus,
} from 'lucide-react';

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [options, setOptions] = useState({ sources: [], types: [], statuses: [] });
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteType, setQuoteType] = useState('customer'); // customer, subcontractor, supplier
  
  // New state for assignments and gallery
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [assignmentForm, setAssignmentForm] = useState({
    member_id: '',
    assignment_type: 'site_inspection',
    description: '',
    assignment_start: '',
    assignment_start_time: '08:00',
    assignment_end: '',
    assignment_end_time: '17:00',
    notes: '',
  });
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Multi-file attachment upload
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const fileInputRef = useRef(null);

  const [convertData, setConvertData] = useState({
    start_date: '',
    end_date: '',
    priority: 'medium',
    additional_notes: '',
  });

  const [quoteForm, setQuoteForm] = useState({
    name: '',
    amount: '',
    file: null,
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadLead();
    loadOptions();
    loadAssignmentTypes();
  }, [id]);

  const loadLead = async () => {
    try {
      const [leadRes, membersRes] = await Promise.all([
        leadsAPI.getOne(id),
        teamMembersAPI.getAll(),
      ]);
      setLead(leadRes.data);
      setEditData(leadRes.data);
      setTeamMembers(membersRes.data.filter(m => m.status === 'active'));
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const res = await leadsAPI.getOptions();
      setOptions(res.data);
    } catch (error) {
      console.error('Failed to load options');
    }
  };

  const loadAssignmentTypes = async () => {
    try {
      const res = await leadsAPI.getAssignmentTypes();
      setAssignmentTypes(res.data);
    } catch (error) {
      console.error('Failed to load assignment types');
    }
  };

  // Assignment handlers
  const openAssignmentModal = (assignment = null) => {
    if (assignment) {
      setEditingAssignment(assignment);
      const startDate = assignment.assignment_start ? new Date(assignment.assignment_start) : null;
      const endDate = assignment.assignment_end ? new Date(assignment.assignment_end) : null;
      
      setAssignmentForm({
        member_id: assignment.member_id,
        assignment_type: assignment.assignment_type || 'site_inspection',
        description: assignment.description || '',
        assignment_start: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        assignment_start_time: startDate ? format(startDate, 'HH:mm') : '08:00',
        assignment_end: endDate ? format(endDate, 'yyyy-MM-dd') : '',
        assignment_end_time: endDate ? format(endDate, 'HH:mm') : '17:00',
        notes: assignment.notes || '',
      });
    } else {
      setEditingAssignment(null);
      setAssignmentForm({
        member_id: '',
        assignment_type: 'site_inspection',
        description: '',
        assignment_start: '',
        assignment_start_time: '08:00',
        assignment_end: '',
        assignment_end_time: '17:00',
        notes: '',
      });
    }
    setShowAssignmentModal(true);
  };

  const handleSaveAssignment = async () => {
    if (!assignmentForm.member_id || !assignmentForm.assignment_start || !assignmentForm.assignment_end) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const startDateTime = `${assignmentForm.assignment_start}T${assignmentForm.assignment_start_time}:00`;
      const endDateTime = `${assignmentForm.assignment_end}T${assignmentForm.assignment_end_time}:00`;
      
      const data = {
        lead_id: id,
        member_id: assignmentForm.member_id,
        assignment_type: assignmentForm.assignment_type,
        description: assignmentForm.description,
        assignment_start: startDateTime,
        assignment_end: endDateTime,
        notes: assignmentForm.notes,
      };

      if (editingAssignment) {
        await leadsAPI.updateAssignment(id, editingAssignment.id, data);
        toast.success('Assignment updated');
      } else {
        await leadsAPI.createAssignment(id, data);
        toast.success('Assignment created');
      }

      setShowAssignmentModal(false);
      setEditingAssignment(null);
      loadLead();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this assignment?')) return;
    
    try {
      await leadsAPI.deleteAssignment(id, assignmentId);
      toast.success('Assignment removed');
      loadLead();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  // Multi-file attachment upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleUploadMultipleAttachments = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploadingAttachments(true);
    try {
      await leadsAPI.uploadMultipleAttachments(id, selectedFiles, attachmentDescription);
      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles([]);
      setAttachmentDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadLead();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingAttachments(false);
    }
  };

  const handleSave = async () => {
    try {
      await leadsAPI.update(id, editData);
      setLead({ ...lead, ...editData });
      setEditing(false);
      toast.success('Lead updated');
      loadLead();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await leadsAPI.update(id, { status: newStatus });
      setLead({ ...lead, status: newStatus });
      toast.success(`Lead status updated to ${newStatus.replace('_', ' ')}`);
      loadLead();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUploadCustomerQuote = async (file) => {
    try {
      await leadsAPI.uploadQuote(id, file);
      toast.success('Quote uploaded');
      loadLead();
    } catch (error) {
      toast.error('Failed to upload quote');
    }
  };

  const handleAddQuote = async () => {
    if (!quoteForm.name || !quoteForm.amount || !quoteForm.file) {
      toast.error('Please fill in all fields and select a file');
      return;
    }

    try {
      if (quoteType === 'subcontractor') {
        await leadsAPI.addSubcontractorQuote(id, quoteForm.name, parseFloat(quoteForm.amount), quoteForm.file);
      } else {
        await leadsAPI.addSupplierQuote(id, quoteForm.name, parseFloat(quoteForm.amount), quoteForm.file);
      }
      toast.success('Quote added');
      setShowQuoteModal(false);
      setQuoteForm({ name: '', amount: '', file: null });
      loadLead();
    } catch (error) {
      toast.error('Failed to add quote');
    }
  };

  const handleConvertToJob = async () => {
    try {
      const res = await leadsAPI.convertToJob(id, convertData);
      toast.success(`Lead converted to job ${res.data.job_code}`);
      setShowConvertModal(false);
      navigate(`/jobs/${res.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    }
  };

  const downloadDocument = (doc, filename) => {
    const link = document.createElement('a');
    link.href = `data:${doc.content_type};base64,${doc.data}`;
    link.download = filename || doc.filename;
    link.click();
  };

  const handleUploadAttachment = async (file) => {
    try {
      await leadsAPI.uploadAttachment(id, file);
      toast.success('Attachment uploaded');
      loadLead();
    } catch (error) {
      toast.error('Failed to upload attachment');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    try {
      await leadsAPI.deleteAttachment(id, attachmentId);
      toast.success('Attachment deleted');
      loadLead();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const statusConfig = {
    new: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, label: 'New' },
    assigned: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: User, label: 'Assigned' },
    started: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: FileText, label: 'Started' },
    closed_won: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Closed Won' },
    closed_lost: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Closed Lost' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading lead...</p>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const config = statusConfig[lead.status] || statusConfig.new;
  const StatusIcon = config.icon;
  const canConvert = lead.status === 'closed_won' && !lead.converted_to_job;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/leads')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Leads
        </button>
        <div className="flex gap-2">
          {/* Gallery Button */}
          <button
            onClick={() => setShowGalleryModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            data-testid="lead-gallery-btn"
          >
            <Image className="w-4 h-4" />
            Gallery
            {lead.gallery?.length > 0 && (
              <span className="bg-purple-400 px-1.5 py-0.5 rounded-full text-xs">{lead.gallery.length}</span>
            )}
          </button>
          {canEdit && !editing && !lead.converted_to_job && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}
          {editing && (
            <>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={() => { setEditing(false); setEditData(lead); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
          {canConvert && (
            <button
              onClick={() => setShowConvertModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              <ArrowRight className="w-4 h-4" />
              Convert to Job
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lead Header */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="text-sm font-mono font-bold text-blue-600">
                  {lead.lead_code}
                </span>
                {editing ? (
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="block text-2xl font-bold text-slate-900 w-full border-b border-blue-500 focus:outline-none mt-1"
                  />
                ) : (
                  <h1 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {lead.title}
                  </h1>
                )}
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
                <StatusIcon className="w-4 h-4" />
                {config.label}
              </span>
            </div>

            {lead.converted_to_job && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-green-700">
                  Converted to Job on {lead.converted_at && format(parseISO(lead.converted_at), 'MMM dd, yyyy')}
                </span>
                <button
                  onClick={() => navigate(`/jobs/${lead.converted_job_id}`)}
                  className="ml-auto text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1"
                >
                  View Job <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-500">Source</label>
                {editing ? (
                  <select
                    value={editData.source}
                    onChange={(e) => setEditData({ ...editData, source: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  >
                    {options.sources.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium text-slate-900">{lead.source}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Type</label>
                {editing ? (
                  <select
                    value={editData.lead_type}
                    onChange={(e) => setEditData({ ...editData, lead_type: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  >
                    {options.types.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium text-slate-900">{lead.lead_type}</p>
                )}
              </div>
            </div>

            {(lead.description || editing) && (
              <div className="mt-4">
                <label className="text-sm text-slate-500">Description</label>
                {editing ? (
                  <textarea
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={3}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="text-slate-700 mt-1 whitespace-pre-wrap break-words">{lead.description}</p>
                )}
              </div>
            )}
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Contact Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-500">Contact Name</label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.contact_name}
                    onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.contact_name}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Company</label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.company_name || ''}
                    onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.company_name || '-'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500 flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> Email
                </label>
                {editing ? (
                  <input
                    type="email"
                    value={editData.contact_email || ''}
                    onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.contact_email || '-'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500 flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" /> Phone
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={editData.contact_phone || ''}
                    onChange={(e) => setEditData({ ...editData, contact_phone: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.contact_phone || '-'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Job Type Selection */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Job Classification
            </h2>
            <div>
              <label className="text-sm text-slate-500">Job Type</label>
              {editing ? (
                <select
                  value={editData.job_type || 'private'}
                  onChange={(e) => setEditData({ ...editData, job_type: e.target.value })}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                >
                  <option value="private">Private</option>
                  <option value="insurance">Insurance</option>
                </select>
              ) : (
                <p className="font-medium text-slate-900 capitalize">{lead.job_type || 'private'}</p>
              )}
            </div>
          </div>

          {/* Insurance Information (conditional) */}
          {((lead.job_type === 'insurance') || (editing && editData.job_type === 'insurance')) && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-purple-600" />
                Insurance Information
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Insurer Name</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_name || ''} onChange={(e) => setEditData({ ...editData, insurer_name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Insurer Contact Person</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_contact_person || ''} onChange={(e) => setEditData({ ...editData, insurer_contact_person: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_contact_person || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Insurer Email</label>
                  {editing ? (
                    <input type="email" value={editData.insurer_email || ''} onChange={(e) => setEditData({ ...editData, insurer_email: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_email || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Insurer Contact No</label>
                  {editing ? (
                    <input type="tel" value={editData.insurer_contact_no || ''} onChange={(e) => setEditData({ ...editData, insurer_contact_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_contact_no || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Reference No</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_reference_no || ''} onChange={(e) => setEditData({ ...editData, insurer_reference_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_reference_no || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Claim No</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_claim_no || ''} onChange={(e) => setEditData({ ...editData, insurer_claim_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.insurer_claim_no || 'N/A'}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-sm text-slate-500">Loss Adjuster Name</label>
                  {editing ? (
                    <input type="text" value={editData.loss_adjuster_name || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_name: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.loss_adjuster_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Loss Adjuster Contact</label>
                  {editing ? (
                    <input type="text" value={editData.loss_adjuster_contact || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_contact: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.loss_adjuster_contact || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Loss Adjuster Contact No</label>
                  {editing ? (
                    <input type="tel" value={editData.loss_adjuster_contact_no || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_contact_no: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.loss_adjuster_contact_no || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Loss Adjuster Email</label>
                  {editing ? (
                    <input type="email" value={editData.loss_adjuster_email || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_email: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{lead.loss_adjuster_email || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Location
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-500">Location</label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.location || ''}
                    onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.location || '-'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Address</label>
                {editing ? (
                  <textarea
                    value={editData.address || ''}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    rows={2}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">{lead.address || '-'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Quotes Section - Always visible */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Quotes & Pricing
              </h2>
              {canEdit && !lead.converted_to_job && (
                <div className="flex gap-2">
                  <button
                    onClick={() => { setQuoteType('subcontractor'); setShowQuoteModal(true); }}
                    className="text-sm px-3 py-1.5 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg"
                  >
                    + Sub-contractor Quote
                  </button>
                  <button
                    onClick={() => { setQuoteType('supplier'); setShowQuoteModal(true); }}
                    className="text-sm px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg"
                  >
                    + Supplier Quote
                  </button>
                </div>
              )}
            </div>

            {/* Quote Amount - Required for conversion */}
            <div className="mb-4">
              <label className="text-sm text-slate-500">
                Quote Amount <span className="text-red-500">*</span>
                <span className="text-xs text-slate-400 ml-1">(Required for job conversion)</span>
              </label>
              {editing ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-slate-500">R</span>
                  <input
                    type="text"
                    value={editData.quote_amount_str !== undefined ? editData.quote_amount_str : (editData.quote_amount || '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setEditData({ 
                        ...editData, 
                        quote_amount_str: value,
                        quote_amount: value ? parseFloat(value) : null 
                      });
                    }}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder="0.00"
                  />
                </div>
              ) : (
                <p className="text-xl font-bold text-slate-900">
                  {lead.quote_amount ? formatCurrency(lead.quote_amount) : <span className="text-slate-400 text-base font-normal">Not set</span>}
                </p>
              )}
            </div>

            {/* Customer Quote Document - Required for conversion */}
            <div className="mb-4">
              <label className="text-sm text-slate-500">
                Customer Quote Document <span className="text-red-500">*</span>
                <span className="text-xs text-slate-400 ml-1">(Required for job conversion)</span>
              </label>
              {lead.quote_document ? (
                <div className="flex items-center gap-2 mt-1 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <Paperclip className="w-4 h-4 text-green-600" />
                  <span className="flex-1 text-sm text-green-700">{lead.quote_document.filename}</span>
                  <button
                    onClick={() => downloadDocument(lead.quote_document)}
                    className="p-1 hover:bg-green-100 rounded"
                  >
                    <Download className="w-4 h-4 text-green-600" />
                  </button>
                </div>
              ) : canEdit && !lead.converted_to_job ? (
                <label className="mt-1 flex items-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-blue-400">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-500">Upload customer quote</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => e.target.files[0] && handleUploadCustomerQuote(e.target.files[0])}
                    />
                  </label>
                ) : (
                  <p className="text-sm text-slate-500 mt-1">No quote uploaded</p>
                )}
            </div>

            {/* Sub-contractor Quotes */}
            {lead.subcontractor_quotes?.length > 0 && (
              <div className="mb-4">
                <label className="text-sm text-slate-500">Sub-contractor Quotes</label>
                <div className="space-y-2 mt-1">
                  {lead.subcontractor_quotes.map((q, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg">
                      <span className="flex-1 text-sm">
                        <span className="font-medium">{q.name}</span> - {formatCurrency(q.amount)}
                      </span>
                      {q.document && (
                        <button
                          onClick={() => downloadDocument(q.document, q.document.filename)}
                          className="p-1 hover:bg-orange-100 rounded"
                        >
                          <Download className="w-4 h-4 text-orange-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supplier Quotes */}
            {lead.supplier_quotes?.length > 0 && (
              <div>
                <label className="text-sm text-slate-500">Supplier Quotes</label>
                <div className="space-y-2 mt-1">
                  {lead.supplier_quotes.map((q, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                      <span className="flex-1 text-sm">
                        <span className="font-medium">{q.name}</span> - {formatCurrency(q.amount)}
                      </span>
                      {q.document && (
                        <button
                          onClick={() => downloadDocument(q.document, q.document.filename)}
                          className="p-1 hover:bg-green-100 rounded"
                        >
                          <Download className="w-4 h-4 text-green-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resource Assignments Section */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Resource Assignments
              </h2>
              {canEdit && !lead.converted_to_job && (
                <button
                  onClick={() => openAssignmentModal()}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                  data-testid="add-lead-assignment-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Assignment
                </button>
              )}
            </div>

            {lead.assignments && lead.assignments.length > 0 ? (
              <div className="space-y-3">
                {lead.assignments.map((assignment) => (
                  <div 
                    key={assignment.id} 
                    className="p-3 bg-slate-50 border border-slate-200 rounded-lg"
                    data-testid={`assignment-${assignment.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{assignment.member_name}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            assignment.status === 'completed' ? 'bg-green-100 text-green-700' :
                            assignment.status === 'active' ? 'bg-blue-100 text-blue-700' :
                            assignment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {assignment.status}
                          </span>
                        </div>
                        <p className="text-sm text-blue-600 font-medium">
                          {assignmentTypes.find(t => t.value === assignment.assignment_type)?.label || assignment.assignment_type}
                        </p>
                        {assignment.description && (
                          <p className="text-sm text-slate-600 mt-1">{assignment.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {assignment.assignment_start && format(parseISO(assignment.assignment_start), 'MMM dd, yyyy HH:mm')}
                          </span>
                          <span>→</span>
                          <span>
                            {assignment.assignment_end && format(parseISO(assignment.assignment_end), 'MMM dd, yyyy HH:mm')}
                          </span>
                        </div>
                        {assignment.notes && (
                          <p className="text-xs text-slate-500 mt-1 italic">{assignment.notes}</p>
                        )}
                      </div>
                      {canEdit && !lead.converted_to_job && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => openAssignmentModal(assignment)}
                            className="p-1.5 hover:bg-slate-200 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteAssignment(assignment.id)}
                            className="p-1.5 hover:bg-red-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">
                No resource assignments yet. Click "Add Assignment" to schedule team members for site inspections, tender briefings, etc.
              </p>
            )}
            
            {lead.converted_to_job && lead.assignments?.length > 0 && (
              <p className="text-xs text-slate-400 mt-3">
                These assignments have been copied to the job.
              </p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Dates */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Dates
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-500">Capture Date</label>
                <p className="font-medium text-slate-900">
                  {format(parseISO(lead.capture_date), 'MMM dd, yyyy')}
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-500">Action Date</label>
                {editing ? (
                  <input
                    type="date"
                    value={editData.action_date ? editData.action_date.split('T')[0] : ''}
                    onChange={(e) => setEditData({ ...editData, action_date: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className="font-medium text-slate-900">
                    {lead.action_date ? format(parseISO(lead.action_date), 'MMM dd, yyyy') : '-'}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Expiry Date</label>
                {editing ? (
                  <input
                    type="date"
                    value={editData.expiry_date ? editData.expiry_date.split('T')[0] : ''}
                    onChange={(e) => setEditData({ ...editData, expiry_date: e.target.value })}
                    className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                  />
                ) : (
                  <p className={`font-medium ${lead.expiry_date && new Date(lead.expiry_date) < new Date() ? 'text-red-600' : 'text-slate-900'}`}>
                    {lead.expiry_date ? format(parseISO(lead.expiry_date), 'MMM dd, yyyy') : '-'}
                    {lead.expiry_date && new Date(lead.expiry_date) < new Date() && ' (Expired)'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Assignment */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Assignment</h2>
            <div>
              <label className="text-sm text-slate-500">Assigned To</label>
              {editing ? (
                <select
                  value={editData.assigned_to || ''}
                  onChange={(e) => setEditData({ ...editData, assigned_to: e.target.value })}
                  className="block w-full px-3 py-2 border border-slate-200 rounded-lg mt-1"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              ) : (
                <p className="font-medium text-slate-900">{lead.assigned_to_name || 'Unassigned'}</p>
              )}
            </div>
          </div>

          {/* Status Actions */}
          {canEdit && !lead.converted_to_job && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Update Status</h2>
              <div className="space-y-2">
                {lead.status === 'new' && (
                  <button
                    onClick={() => handleStatusChange('assigned')}
                    className="w-full py-2 px-4 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm"
                  >
                    Mark as Assigned
                  </button>
                )}
                {(lead.status === 'new' || lead.status === 'assigned') && (
                  <button
                    onClick={() => handleStatusChange('started')}
                    className="w-full py-2 px-4 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg text-sm"
                  >
                    Mark as Started
                  </button>
                )}
                {lead.status !== 'closed_won' && lead.status !== 'closed_lost' && (
                  <>
                    <button
                      onClick={() => handleStatusChange('closed_won')}
                      className="w-full py-2 px-4 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Close as Won
                    </button>
                    <button
                      onClick={() => handleStatusChange('closed_lost')}
                      className="w-full py-2 px-4 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Close as Lost
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Convert to Job */}
          {canConvert && (
            <div className={`border rounded-xl p-6 ${
              lead.quote_amount && lead.quote_document
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              <h2 className={`text-lg font-semibold mb-2 ${
                lead.quote_amount && lead.quote_document ? 'text-green-800' : 'text-amber-800'
              }`}>
                {lead.quote_amount && lead.quote_document ? 'Ready to Convert!' : 'Requirements Missing'}
              </h2>
              
              {/* Requirements checklist */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  {lead.quote_amount ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  )}
                  <span className={lead.quote_amount ? 'text-green-700' : 'text-amber-700'}>
                    Quote amount: {lead.quote_amount ? formatCurrency(lead.quote_amount) : 'Not set'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  {lead.quote_document ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  )}
                  <span className={lead.quote_document ? 'text-green-700' : 'text-amber-700'}>
                    Quote document: {lead.quote_document ? 'Attached' : 'Not attached'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Sub-contractor and supplier quotes are optional
                </p>
              </div>

              <button
                onClick={() => setShowConvertModal(true)}
                disabled={!lead.quote_amount || !lead.quote_document}
                className={`w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 ${
                  lead.quote_amount && lead.quote_document
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                }`}
              >
                <ArrowRight className="w-4 h-4" />
                Convert to Job
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Notes</h2>
            {editing ? (
              <textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            ) : (
              <p className="text-slate-600">{lead.notes || 'No notes.'}</p>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Attachments
            </h2>
            
            {/* Multi-file upload */}
            {canEdit && !lead.converted_to_job && (
              <div className="mb-4 bg-slate-50 p-4 rounded-lg">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Select Files (multiple)
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      data-testid="lead-attachment-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Description (applies to all files)
                    </label>
                    <input
                      type="text"
                      value={attachmentDescription}
                      onChange={(e) => setAttachmentDescription(e.target.value)}
                      placeholder="e.g., Site photos, Quotation docs..."
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleUploadMultipleAttachments}
                    disabled={selectedFiles.length === 0 || uploadingAttachments}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg text-sm"
                    data-testid="lead-upload-attachments-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Upload {selectedFiles.length > 0 && `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})`}
                  </button>
                  {selectedFiles.length > 0 && (
                    <p className="text-xs text-slate-500">
                      Selected: {selectedFiles.map(f => f.name).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Attachment list */}
            {lead.attachments?.length > 0 ? (
              <div className="space-y-2">
                {lead.attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-lg">
                    <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{att.filename}</p>
                      {att.description && (
                        <p className="text-xs text-slate-500 truncate">{att.description}</p>
                      )}
                      <p className="text-xs text-slate-400">
                        {att.uploaded_by_name && `by ${att.uploaded_by_name} • `}
                        {att.uploaded_at && format(parseISO(att.uploaded_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <button
                      onClick={() => downloadDocument(att, att.filename)}
                      className="p-1 hover:bg-slate-200 rounded flex-shrink-0"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-slate-600" />
                    </button>
                    {canEdit && !lead.converted_to_job && (
                      <button
                        onClick={() => handleDeleteAttachment(att.id)}
                        className="p-1 hover:bg-red-100 rounded flex-shrink-0"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No attachments yet.</p>
            )}
            
            {lead.converted_to_job && lead.attachments?.length > 0 && (
              <p className="text-xs text-slate-400 mt-2">
                These attachments have been copied to the job.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Convert to Job Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-green-600" />
                Convert Lead to Job
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                This will create a new job from <strong>{lead.lead_code}</strong>. All lead details, contact info, and attached quotes will be copied to the job.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Job Start Date</label>
                  <input
                    type="date"
                    value={convertData.start_date}
                    onChange={(e) => setConvertData({ ...convertData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Job End Date</label>
                  <input
                    type="date"
                    value={convertData.end_date}
                    onChange={(e) => setConvertData({ ...convertData, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                <select
                  value={convertData.priority}
                  onChange={(e) => setConvertData({ ...convertData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowConvertModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvertToJob}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <ArrowRight className="w-4 h-4" />
                  Convert to Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Add {quoteType === 'subcontractor' ? 'Sub-contractor' : 'Supplier'} Quote
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {quoteType === 'subcontractor' ? 'Sub-contractor' : 'Supplier'} Name *
                </label>
                <input
                  type="text"
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quote Amount (R) *</label>
                <input
                  type="text"
                  value={quoteForm.amount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    setQuoteForm({ ...quoteForm, amount: value });
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quote Document *</label>
                <input
                  type="file"
                  onChange={(e) => setQuoteForm({ ...quoteForm, file: e.target.files[0] })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => { setShowQuoteModal(false); setQuoteForm({ name: '', amount: '', file: null }); }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddQuote}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Add Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                {editingAssignment ? 'Edit Assignment' : 'Add Resource Assignment'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Team Member *</label>
                <select
                  value={assignmentForm.member_id}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, member_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  data-testid="assignment-member-select"
                >
                  <option value="">Select a team member</option>
                  {teamMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Assignment Type *</label>
                <select
                  value={assignmentForm.assignment_type}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {assignmentTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <input
                  type="text"
                  value={assignmentForm.description}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                  placeholder="e.g., Roof inspection with client"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_start}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_start: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={assignmentForm.assignment_start_time}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_end}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_end: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">End Time</label>
                  <input
                    type="time"
                    value={assignmentForm.assignment_end_time}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Additional notes..."
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => { setShowAssignmentModal(false); setEditingAssignment(null); }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignment}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                  data-testid="save-lead-assignment-btn"
                >
                  {editingAssignment ? 'Update' : 'Create'} Assignment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      <LeadGalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        leadId={id}
        leadCode={lead?.lead_code}
        canEdit={canEdit && !lead?.converted_to_job}
        onUpdate={loadLead}
      />
    </div>
  );
};

export default LeadDetail;
